import {
  PAGE_HIDE,
  RENDERING_ERROR,
  UNLOAD_FRAME_ERROR,
  VISIBILITY_CHANGE_HIDDEN
} from '../../@types/supply-monitoring-events.type';

type SendMonitoringEvent = (
  event: SUPPLY_MONITORING_EVENT_TYPE,
  fields?: OptionalSupplyMonitoringEventFields
) => void | Promise<void>;

export class FrameLifecycleService {
  pageHideHandler = this._pageHideEvent.bind(this);
  visibilityEventHandler = this._visibilityChangeEvent.bind(this);

  constructor(private readonly sendMonitoringEvent: SendMonitoringEvent) {}

  listen() {
    const options = {
      passive: true
    };

    window.addEventListener('pagehide', this.pageHideHandler, options);

    window.addEventListener(
      'visibilitychange',
      this.visibilityEventHandler,
      options
    );
  }

  destroy() {
    window.removeEventListener('pagehide', this.pageHideHandler);
    this._removeVisibilityChangeEventListener();
  }

  private _pageHideEvent(): void {
    // if the top window is still visible, it means only the frame has been refreshed, so we send the rendering error event
    if (this._isTopWindowVisible()) {
      this.sendMonitoringEvent(RENDERING_ERROR, {
        error_type: UNLOAD_FRAME_ERROR
      });
      return;
    }

    this.sendMonitoringEvent(PAGE_HIDE, this._getAdditionalFields());
  }

  private _visibilityChangeEvent(): void {
    if (document.visibilityState === 'visible') return;

    this.sendMonitoringEvent(
      VISIBILITY_CHANGE_HIDDEN,
      this._getAdditionalFields()
    );

    this._removeVisibilityChangeEventListener();
  }

  private _isTopWindowVisible(): boolean {
    try {
      return window.top?.document.visibilityState === 'visible';
    } catch (e) {
      return false;
    }
  }

  private _getAdditionalFields():
    | AdditionalSupplyMonitoringEventFields
    | undefined {
    const timeSpentOnPage = this._getTimeSpentOnPage();
    if (!timeSpentOnPage) return;

    return { details: JSON.stringify({ timeSpentOnPage }) };
  }

  private _getTimeSpentOnPage(): number {
    return document.timeline?.currentTime as number;
  }

  private _removeVisibilityChangeEventListener() {
    window.removeEventListener('visibilitychange', this.visibilityEventHandler);
  }
}
